import { useCallback, useMemo, useState, useContext } from 'react';
import { Link as RouterLink, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Box, Form, Typography, TextButton, useMediaQuery } from 'bonfire-kindling';

import axios from 'helpers/axios';
import { Page } from 'components';
import { ToastContext } from 'contexts';
import { RouterPrompt, BrowserPrompt } from 'components';
import { PROJECT_SOURCE, PROJECT_STATUS, PROJECT_VIEW_STATUS } from 'constants/opportunities';
import { SEARCH_PROFILES, SEARCH_PROFILES_ID } from 'constants/apis';
import {
  useOpportunityTypes,
  SAVED_SEARCH_PROFILES_KEYS,
  useSpecificSavedSearchProfile,
  OPPORTUNITIES_SEARCH_QUERY_KEYS,
} from 'helpers/opportunities';
import {
  validateFormValues,
  preprocessFormValues,
  processExistingProfile,
  processOpportunitiesFilters,
} from '../utils';
import { useControlledAccordions, useSavedSearchFields } from '../hooks';
import SavedSearchSettingsUpsertTabs from './SavedSearchSettingsUpsertTabs';
import SavedSearchSettingsUpsertAccordions from './SavedSearchSettingsUpsertAccordions';

const SEARCH_TERM_FIELDS = ['keywords', 'excludedKeywords'];

const SavedSearchSettingsUpsert = () => {
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);
  const isTabletOrDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch('/settings/saved/:id/edit');
  const existingProfileId = match ? parseInt(match.params.id, 10) : null;

  const { fields, fieldIds } = useSavedSearchFields();
  const { onToggleExpand, ...accordionsState } = useControlledAccordions({ fieldIds });
  const [currentTab, setCurrentTab] = useState(fieldIds.name);

  const { data: opportunityTypes } = useOpportunityTypes();
  const existingProfileQuery = useSpecificSavedSearchProfile(existingProfileId, {
    onError: () => history.push('/settings/saved'),
  });

  const { mutateAsync } = useMutation(({ id, payload }) => {
    return id ? axios.patch(SEARCH_PROFILES_ID(id), payload) : axios.post(SEARCH_PROFILES, payload);
  });

  const onSubmit = useCallback(
    async values => {
      const errors = validateFormValues(values);

      if (errors) {
        Object.keys(errors).forEach((field, idx) => {
          // keywords/excludedKeywords are grouped under `searchTerms`.
          const fieldId = SEARCH_TERM_FIELDS.includes(field)
            ? fieldIds.searchTerms
            : fieldIds[field];

          if (idx === 0) {
            setCurrentTab(fieldId);
          }

          onToggleExpand(true, fieldId);
        });

        return errors;
      }

      await mutateAsync(
        { id: existingProfileId, payload: preprocessFormValues(values) },
        {
          onError: () => triggerToast({ message: t('generic_error') }),
          onSuccess: result => {
            queryClient.invalidateQueries(SAVED_SEARCH_PROFILES_KEYS.summary);
            queryClient.setQueryData(SAVED_SEARCH_PROFILES_KEYS.id(result.data.id), result.data);
            queryClient.resetQueries(OPPORTUNITIES_SEARCH_QUERY_KEYS.profile(result.data.id), {
              exact: false,
            });
            history.push('/settings/saved');
          },
        }
      );
    },
    [
      mutateAsync,
      existingProfileId,
      onToggleExpand,
      fieldIds,
      triggerToast,
      t,
      queryClient,
      history,
    ]
  );

  const initialValues = useMemo(() => {
    return {
      name: '',
      taxonomyCodes: [],
      locations: [],
      opportunityTypes: opportunityTypes ?? [],
      viewedProjects: PROJECT_VIEW_STATUS.allStatuses,
      status: PROJECT_STATUS.allOpportunities,
      opportunitySource: PROJECT_SOURCE.allSources,
      organizations: [],
      keywords: '',
      excludedKeywords: '',
      // The order here is important depending on if the user is overriding or
      // creating a new search via the 'Opportunities' page.
      ...processExistingProfile(existingProfileQuery.data),
      ...processOpportunitiesFilters(location.state?.opportunitiesFilter),
    };
  }, [opportunityTypes, location.state, existingProfileQuery.data]);

  return (
    <Page>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: theme => ({ xs: `${theme.spacing(6)} ${theme.spacing(3)}`, lg: '5.5rem 12rem' }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isTabletOrDesktop ? 'row' : 'column-reverse',
            alignItems: isTabletOrDesktop ? 'center' : 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography variant={isTabletOrDesktop ? 'h1' : 'h3'} component="h2">
            {t(
              existingProfileId
                ? 'settings.saved_search.edit_or_create.edit_title'
                : 'settings.saved_search.edit_or_create.add_title'
            )}
          </Typography>
          <TextButton
            label={t('settings.saved_search.edit_or_create.back')}
            component={RouterLink}
            to="/settings/saved"
            iconLeft={isTabletOrDesktop ? undefined : 'chevron-left'}
            variant={isTabletOrDesktop ? 'outline' : 'text'}
            sx={{
              ...(isTabletOrDesktop
                ? {}
                : { color: theme => theme.palette.primary.main, fontWeight: 'bold', mb: 4 }),
            }}
          />
        </Box>
        <Form onSubmit={onSubmit} initialValues={initialValues}>
          {({ handleSubmit, pristine, submitting }) => (
            <form onSubmit={handleSubmit} noValidate>
              {isTabletOrDesktop ? (
                <SavedSearchSettingsUpsertTabs
                  fields={fields}
                  currentTab={currentTab}
                  onTabChange={setCurrentTab}
                />
              ) : (
                <SavedSearchSettingsUpsertAccordions
                  fields={fields}
                  onToggleExpand={onToggleExpand}
                  {...accordionsState}
                />
              )}
              <Box
                sx={{
                  ...(isTabletOrDesktop
                    ? {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        width: '100%',
                      }
                    : {}),
                }}
              >
                <TextButton
                  label={t('settings.saved_search.edit_or_create.cancel')}
                  component={RouterLink}
                  to="/settings/saved"
                  variant="outline"
                  sx={{ background: theme => theme.palette.common.white }}
                  fullWidth={!isTabletOrDesktop}
                />
                <TextButton
                  type="submit"
                  label={t('settings.saved_search.edit_or_create.save_search')}
                  sx={{ mt: isTabletOrDesktop ? 0 : 3, ml: isTabletOrDesktop ? 3 : 0 }}
                  fullWidth={!isTabletOrDesktop}
                  isBusy={submitting}
                />
              </Box>
              <BrowserPrompt when={!pristine && !submitting} />
              <RouterPrompt
                when={!pristine && !submitting}
                title={t('settings.saved_search.edit_or_create.unsaved_modal_title')}
                CancelButtonProps={{
                  label: t('settings.saved_search.edit_or_create.unsaved_modal_cancel'),
                }}
                ConfirmButtonProps={{
                  label: t('settings.saved_search.edit_or_create.unsaved_modal_continue'),
                }}
              />
            </form>
          )}
        </Form>
      </Box>
    </Page>
  );
};

export default SavedSearchSettingsUpsert;
